import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useScopedPath } from '../ScopedLink';
import { useListLiveSessionsQuery } from '../../store/api/localApi';
import { PlayCircleIcon, UserGroupIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface LiveSessionsStripProps {
  locationId: string;
}

/**
 * "Sessions running right now" board for the top of the staff/admin dashboard.
 *
 * Sessions are multi-staff (ratio cover), so this is the way anyone on the
 * floor rejoins a session that's already open — no more losing a session by
 * navigating away. The backend (GET /sessions/live) already scopes what comes
 * back by role: admins see every live session at the location, plain staff only
 * the ones for classes they're assigned to. We render whatever it returns.
 *
 * Renders nothing when there are no live sessions, so it never adds noise to a
 * quiet dashboard.
 */
const LiveSessionsStrip: React.FC<LiveSessionsStripProps> = ({ locationId }) => {
  const navigate = useNavigate();
  const scopedPath = useScopedPath();
  const { data } = useListLiveSessionsQuery(
    { locationId },
    // Live board: poll so a session another staffer starts shows up without a
    // manual refresh, and refetch when the tab regains focus.
    { pollingInterval: 30000, refetchOnFocus: true }
  );

  const sessions = data?.sessions ?? [];
  if (sessions.length === 0) return null;

  return (
    <section className="mt-6" data-testid="live-sessions-strip" aria-label="Live sessions">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
        </span>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Live now</h3>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sessions.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`live-session-${s.id}`}
            onClick={() => navigate(scopedPath(`/sessions/${s.id}`))}
            className="group text-left bg-white border border-green-200 shadow-sm rounded-lg p-4 hover:shadow-md hover:border-green-300 transition-shadow focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-base font-semibold text-gray-900 truncate">
                  <PlayCircleIcon className="h-5 w-5 text-green-600 shrink-0" aria-hidden="true" />
                  <span className="truncate" data-testid={`live-session-name-${s.id}`}>
                    {s.className}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {s.scheduledStart}–{s.scheduledEnd}
                  {s.startedByName ? ` · Started by ${s.startedByName}` : ''}
                </p>
              </div>
              <span
                className="inline-flex items-center gap-1 shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
                data-testid={`live-session-present-${s.id}`}
              >
                <UserGroupIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {s.presentCount}/{s.expectedCount}
              </span>
            </div>

            <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-green-700 group-hover:gap-1.5 transition-all">
              Open
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};

export default LiveSessionsStrip;
